import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

// Matches the API's Child schema
export interface Child {
  id: string
  familyId: string
  firstName: string
  dateOfBirth: string | null
  createdAt: string
}

export interface Family {
  id: string
  name: string
  children: Child[]
}

interface FamilyState {
  family: Family | null
  activeChildId: string | null

  // Derived — use this in components
  activeChild: Child | null

  // Actions
  setFamily: (family: Family) => void
  setActiveChild: (childId: string) => void
  addChild: (child: Child) => void
  updateChild: (childId: string, updates: Partial<Child>) => void
  removeChild: (childId: string) => void
  reset: () => void
}

const initialState = {
  family: null,
  activeChildId: null,
  activeChild: null,
}

export const useFamilyStore = create<FamilyState>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setFamily: (family) =>
          set(() => {
            const activeChildId =
              get().activeChildId ?? family.children[0]?.id ?? null
            return {
              family,
              activeChildId,
              activeChild:
                family.children.find((c) => c.id === activeChildId) ?? null,
            }
          }),

        setActiveChild: (childId) =>
          set((state) => ({
            activeChildId: childId,
            activeChild:
              state.family?.children.find((c) => c.id === childId) ?? null,
          })),

        addChild: (child) =>
          set((state) => {
            if (!state.family) return state
            const children = [...state.family.children, child]
            return {
              family: { ...state.family, children },
              activeChildId: state.activeChildId ?? child.id,
              activeChild: state.activeChild ?? child,
            }
          }),

        updateChild: (childId, updates) =>
          set((state) => {
            if (!state.family) return state
            const children = state.family.children.map((c) =>
              c.id === childId ? { ...c, ...updates } : c
            )
            const updatedActive =
              state.activeChildId === childId
                ? children.find((c) => c.id === childId) ?? null
                : state.activeChild
            return {
              family: { ...state.family, children },
              activeChild: updatedActive,
            }
          }),

        removeChild: (childId) =>
          set((state) => {
            if (!state.family) return state
            const children = state.family.children.filter((c) => c.id !== childId)
            const wasActive = state.activeChildId === childId
            const newActiveChild = wasActive ? (children[0] ?? null) : state.activeChild
            return {
              family: { ...state.family, children },
              activeChildId: newActiveChild?.id ?? null,
              activeChild: newActiveChild,
            }
          }),

        reset: () => set(initialState),
      }),
      {
        name: 'birchfolio-family',
        // Only persist the active child selection, not full family data
        // (family data is re-fetched from the API on load)
        partialize: (state) => ({ activeChildId: state.activeChildId }),
      }
    ),
    { name: 'family-store' }
  )
)
